import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createKnowledgeIngest, getGoogleDriveIngestInfo } from '../../api/knowledgeBases';
import { getApiErrorMessage } from '../../utils/errors';
import KnowledgeIngestForm from './components/KnowledgeIngestForm';
import KnowledgeStatusBadge from './components/KnowledgeStatusBadge';
import { DEFAULT_INGEST_SOURCE } from './constants';

function KnowledgeBaseNew() {
  const location = useLocation();
  const [googleDriveInfo, setGoogleDriveInfo] = useState(null);
  const [oauthNotice, setOauthNotice] = useState('');
  const [oauthError, setOauthError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdKb, setCreatedKb] = useState(null);

  useEffect(() => {
    getGoogleDriveIngestInfo()
      .then(setGoogleDriveInfo)
      .catch(() => setGoogleDriveInfo(null));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('google_drive') === 'connected') {
      setOauthNotice('Google Drive conectado com sucesso.');
      setOauthError('');
    } else if (params.get('google_drive') === 'error') {
      setOauthError(params.get('message') || 'Falha ao conectar Google Drive.');
      setOauthNotice('');
    }
  }, [location.search]);

  const handleSubmit = async (payload) => {
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const kb = await createKnowledgeIngest(payload);
      setCreatedKb(kb);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Não foi possível cadastrar a base.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageNotice = useMemo(() => {
    if (oauthError) {
      return { type: 'error', message: oauthError };
    }
    if (oauthNotice) {
      return { type: 'success', message: oauthNotice };
    }
    return null;
  }, [oauthError, oauthNotice]);

  if (createdKb) {
    return (
      <div className="space-y-6 max-w-lg mx-auto">
        <div className="surface-card p-8 shadow-card-md text-center">
          <KnowledgeStatusBadge status={createdKb.status} />
          <h1 className="text-xl font-bold text-content-primary mt-4 mb-2">Base cadastrada</h1>
          <p className="text-content-muted text-sm mb-6">
            <strong>{createdKb.name}</strong> foi cadastrada. Em breve o conteúdo será processado.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/knowledge-bases" className="btn-primary text-sm px-4 py-2.5">
              Ver bases de conhecimento
            </Link>
            <Link
              to={`/knowledge-bases/${createdKb.id}`}
              className="btn-secondary text-sm px-4 py-2.5"
            >
              Ver detalhes
            </Link>
            <button
              type="button"
              onClick={() => {
                setCreatedKb(null);
                setSubmitError('');
                setOauthNotice('');
                setOauthError('');
              }}
              className="btn-secondary text-sm px-4 py-2.5"
            >
              Cadastrar outra
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div>
        <Link to="/knowledge-bases" className="text-sm text-content-muted hover:text-forge-600">
          ← Voltar para bases
        </Link>
        <h1 className="text-2xl font-bold text-content-primary mt-3">Nova base de conhecimento</h1>
        <p className="text-content-muted text-sm mt-1">
          Conecte uma pasta do Google Drive ou escolha outra origem para o conteúdo.
        </p>
      </div>

      <div className="surface-card p-8 shadow-card-md">
        {pageNotice && (
          <div
            className={`rounded-xl border px-4 py-3 mb-4 ${
              pageNotice.type === 'error'
                ? 'border-red-200 bg-red-50'
                : 'border-emerald-200 bg-emerald-50'
            }`}
          >
            <p
              className={`text-sm ${
                pageNotice.type === 'error' ? 'text-red-700' : 'text-emerald-700'
              }`}
            >
              {pageNotice.message}
            </p>
          </div>
        )}
        <KnowledgeIngestForm
          initialValues={{ source: DEFAULT_INGEST_SOURCE }}
          googleDriveInfo={googleDriveInfo}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={submitError}
        />
      </div>
    </div>
  );
}

export default KnowledgeBaseNew;
